/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/binding 层；
 * 2. 负责实现“草图边/线/曲线等子实体 -> 特征输入”的绑定对象；
 * 3. 当前轮次主要为后续扩展预留统一协议，避免以后再绕回隐式标签补丁式开发。
 *
 * 文件职责：
 * 1. 封装草图子实体引用的序列化和克隆能力；
 * 2. 在当前 Viewer 场景中解析目标子实体所属对象；
 * 3. 为未来旋转轴、参考几何、端点级驱动特征提供统一绑定入口。
 */

import {
  FeatureReferenceBinding,
  type FeatureBindingResolveContext,
  type FeatureBindingResolvedValue,
  type SerializedSketchSubEntityFeatureBinding,
} from './FeatureReferenceBinding';
import {
  assertSketchEntityPersistentId,
  assertSketchPersistentId,
} from '../../sketch/identity/SketchPersistentIdFactory';

/**
 * 草图子实体绑定对象。
 */
export class SketchSubEntityFeatureBinding extends FeatureReferenceBinding {
  /** 绑定类型。 */
  public readonly kind = 'sketch-sub-entity' as const;

  /** 序列化载荷。 */
  private readonly payload: SerializedSketchSubEntityFeatureBinding;

  /**
   * 构造函数。
   *
   * @param payload 绑定载荷。
   */
  public constructor(payload: SerializedSketchSubEntityFeatureBinding) {
    super();
    assertSketchPersistentId(payload.sketchId, 'sketch-sub-entity.sketchId');
    assertSketchEntityPersistentId(
      payload.sourceRef.ownerObjectId,
      'sketch-sub-entity.sourceRef.ownerObjectId',
    );
    if (!payload.sourceRef.ownerSketchId) {
      throw new Error('sketch-sub-entity.sourceRef.ownerSketchId 不能为空。');
    }
    assertSketchPersistentId(
      payload.sourceRef.ownerSketchId,
      'sketch-sub-entity.sourceRef.ownerSketchId',
    );
    if (payload.sourceRef.ownerSketchId !== payload.sketchId) {
      throw new Error('sketch-sub-entity 的 sketchId 与 ownerSketchId 必须一致。');
    }
    this.payload = structuredClone(payload);
  }

  /**
   * @inheritdoc
   */
  public serialize(): SerializedSketchSubEntityFeatureBinding {
    return structuredClone(this.payload);
  }

  /**
   * @inheritdoc
   */
  public clone(): SketchSubEntityFeatureBinding {
    return new SketchSubEntityFeatureBinding(this.serialize());
  }

  /**
   * @inheritdoc
   */
  public dependsOnSketch(sketchId: string): boolean {
    return this.payload.sketchId === sketchId;
  }

  /**
   * @inheritdoc
   */
  public resolve(context: FeatureBindingResolveContext): FeatureBindingResolvedValue | null {
    const sketchGroup = context.viewer.sketchGroupManager?.getGroupById(this.payload.sketchId) ?? null;
    if (!sketchGroup) {
      return null;
    }

    const targetObject = sketchGroup.getObjectByPersistentId(
      this.payload.sourceRef.ownerObjectId,
    );

    return {
      kind: 'sketch-sub-entity',
      sketchId: this.payload.sketchId,
      sourceRef: structuredClone(this.payload.sourceRef),
      object: targetObject,
    };
  }
}
